import { useEffect, useMemo } from 'react';
import { Box, Spinner, Text, Title } from '@nimbus-ds/components';
import { Page } from '@nimbus-ds/patterns';
import { useLocation } from 'react-router';
import { Country } from '@tiendanube/common/src/enums';
import { useNavegate } from 'App/hooks';
import { useToast } from 'commons/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import { ORDERS_SECTION_ROUTES } from 'domains/Orders';
import useNuvemEnvioTranslations from '../../../hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import useCreateNuvemEnvioLabels from '../../hooks/useCreateNuvemEnvioLabels/useCreateNuvemEnvioLabels';

function OrdersLabelGenerateStatusPage(): JSX.Element {
  const t = useNuvemEnvioTranslations();
  const { createNuvemEnvioLabels, resetNuvemEnvioLabels, data, status } =
    useCreateNuvemEnvioLabels();
  const location = useLocation();
  const country = useGetCountry();

  const { goTo } = useNavegate();
  const { addToast } = useToast();

  const searchParams = new URLSearchParams(location.search);

  const idsParam = searchParams.get('ids');
  const ids = useMemo(() => (idsParam ? idsParam.split(',') : []), [idsParam]);

  useEffect(() => {
    resetNuvemEnvioLabels();
  }, [resetNuvemEnvioLabels]);

  useEffect(() => {
    if (ids.length > 0) {
      createNuvemEnvioLabels({
        createFile: {
          label: true,
          contentDeclaration: country === Country.BR ? true : false,
        },
        fulfillmentOrderIds: ids,
      });
    }
  }, [ids, createNuvemEnvioLabels, resetNuvemEnvioLabels, country]);

  useEffect(() => {
    if (status.isSuccess) {
      const openInNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
      };

      data?.contentDeclarationUrls?.forEach(openInNewTab);
      data?.labelUrls?.forEach(openInNewTab);

      if (status.isError || (data?.errors && data?.errors.length > 0)) {
        addToast({
          label: t('printNuvemEnvioLabelsStatus.toast.error'),
          appearance: 'danger',
        });
      } else {
        addToast({
          label: t('printNuvemEnvioLabelsStatus.toast.success'),
          appearance: 'success',
        });
      }

      resetNuvemEnvioLabels();
      goTo(ORDERS_SECTION_ROUTES.orderList);
    } else if (status?.isError) {
      resetNuvemEnvioLabels();

      addToast({
        label: t('printNuvemEnvioLabelsStatus.toast.error'),
        appearance: 'danger',
      });

      goTo(ORDERS_SECTION_ROUTES.orderList);
    }
  }, [status, goTo, data, addToast, ids, t, resetNuvemEnvioLabels]);

  return (
    <Page>
      <Page.Body height="90vh" display="flex" alignItems="center">
        <Box
          m="auto"
          width={{
            md: '100%',
            xs: '340px',
          }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="4"
        >
          <Spinner size="large" color="neutral-textLow" />
          <Title as="h4">
            {t('printNuvemEnvioLabelsStatus.processing.title')}
          </Title>
          <Text textAlign="center">
            {t('printNuvemEnvioLabelsStatus.processing.description')}
          </Text>
        </Box>
      </Page.Body>
    </Page>
  );
}

export default OrdersLabelGenerateStatusPage;
